import { apiFetch } from './client.js';
import { DEMO } from '../demo/index.js';

// =====================================================================
// Tipos públicos (espejo de los del backend)
// =====================================================================

export interface Citation {
  fileId: string;
  fileName: string;
  pageNum?: number | null;
  chunkId?: number;
  score?: number;
  snippet?: string;
}

export interface PersistedToolCall {
  id: string;
  name: string;
  arguments: string;
  result?: string;
  startedAt?: string;
  endedAt?: string;
}

export interface ChatMessage {
  id: string;
  conversationId: string;
  role: 'system' | 'user' | 'assistant' | 'tool';
  content: string;
  citations: Citation[];
  toolCalls: PersistedToolCall[];
  tokenUsage: { input?: number; output?: number } | null;
  model: string | null;
  toolName: string | null;
  createdAt: string;
}

export interface Conversation {
  id: string;
  projectId: string;
  userId: string;
  title: string | null;
  createdAt: string;
  updatedAt: string;
  lastMessageAt: string | null;
}

// =====================================================================
// REST: conversaciones
// =====================================================================

export async function listConversations(projectId: string) {
  const r = await apiFetch<{ conversations: Conversation[] }>(
    `/api/v1/projects/${projectId}/conversations`,
  );
  return r.conversations;
}

export async function createConversation(projectId: string, title?: string | null) {
  const r = await apiFetch<{ conversation: Conversation }>(
    `/api/v1/projects/${projectId}/conversations`,
    { method: 'POST', body: { title: title ?? null } },
  );
  return r.conversation;
}

export async function getConversation(projectId: string, conversationId: string) {
  return apiFetch<{ conversation: Conversation; messages: ChatMessage[] }>(
    `/api/v1/projects/${projectId}/conversations/${conversationId}`,
  );
}

export async function deleteConversation(projectId: string, conversationId: string) {
  return apiFetch<{ deleted: boolean }>(
    `/api/v1/projects/${projectId}/conversations/${conversationId}`,
    { method: 'DELETE' },
  );
}

// =====================================================================
// SSE streaming
//
// Por qué fetch en vez de EventSource: necesitamos POST con body JSON y
// EventSource sólo soporta GET. Hacemos un parser SSE muy chico sobre el
// ReadableStream de la respuesta.
// =====================================================================

export type ChatStreamEvent =
  | { type: 'user_message_persisted'; messageId: string }
  | { type: 'retrieval'; hits: Citation[] }
  | { type: 'tool_call_start'; id: string; name: string }
  | { type: 'tool_call_args_delta'; id: string; deltaJson: string }
  | { type: 'tool_call_complete'; id: string; name: string; arguments: string; result: string }
  | { type: 'token'; delta: string }
  | { type: 'assistant_message_persisted'; messageId: string; citations: Citation[]; model: string }
  | { type: 'usage'; input?: number; output?: number }
  | { type: 'done' }
  | { type: 'error'; error: string };

export interface SendMessageArgs {
  projectId: string;
  conversationId: string;
  content: string;
  signal?: AbortSignal;
  onEvent: (ev: ChatStreamEvent) => void;
}

export async function sendMessageStream(args: SendMessageArgs): Promise<void> {
  // MODO DEMO: stream falso con respuestas del guion (misma UI, sin red).
  if (DEMO) {
    const { demoChatStream } = await import('../demo/chatDemo.js');
    return demoChatStream(args);
  }
  const res = await fetch(
    `/api/v1/projects/${args.projectId}/conversations/${args.conversationId}/messages`,
    {
      method: 'POST',
      credentials: 'include',
      headers: {
        'content-type': 'application/json',
        accept: 'text/event-stream',
      },
      body: JSON.stringify({ content: args.content }),
      signal: args.signal,
    },
  );

  if (!res.ok || !res.body) {
    const txt = await res.text().catch(() => '');
    throw new Error(`Chat stream error ${res.status}: ${txt.slice(0, 200)}`);
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder('utf-8');
  let buffer = '';
  let currentEvent = '';
  let currentData = '';

  const flush = () => {
    if (!currentEvent || !currentData) {
      currentEvent = '';
      currentData = '';
      return;
    }
    try {
      const parsed = JSON.parse(currentData);
      args.onEvent({ type: currentEvent, ...parsed } as ChatStreamEvent);
    } catch (err) {
      // mensaje de comentario o malformado — ignorar
      console.warn('SSE parse error', err, currentData);
    }
    currentEvent = '';
    currentData = '';
  };

  while (true) {
    const { value, done } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });

    let idx;
    while ((idx = buffer.indexOf('\n')) >= 0) {
      const line = buffer.slice(0, idx).replace(/\r$/, '');
      buffer = buffer.slice(idx + 1);

      if (line === '') {
        flush();
        continue;
      }
      if (line.startsWith(':')) continue; // keep-alive comment
      if (line.startsWith('event:')) currentEvent = line.slice(6).trim();
      else if (line.startsWith('data:')) currentData += line.slice(5).trim();
    }
  }
  // último flush por si no terminó en \n\n
  flush();
}
